import { useEffect, useRef } from 'react';
import * as React from 'react';
import styled from 'styled-components';
import Row from '@sdk/components/ui/spacing/Row';
import View from '@sdk/components/primitives/View';
import Padding from '@sdk/components/ui/spacing/Padding';
import Clickable from '@sdk/components/primitives/Clickable';
import EditablePicker from '@sdk/components/forms/EditablePicker';
import { AlarmUnits } from '@sdk/shifts/types/shiftAlarmsType';
import type { ShiftAlarmModelType } from '@sdk/shifts/types/shiftAlarmsType';

import TrashIcon from '../../../../assets/svg/UI/trash.svg';
import {
  SHIFT_ALARM_OPTIONS_IN_MINUTES,
  SHIFT_ALARMS_UNIT_OPTIONS,
  SHIFT_ALARM_OPTIONS_IN_HOURS,
} from '../../../constants';
import { useSettingsErrorsContext } from '../../../SettingsErrorContext';
import { TabType } from '../../../types';

import type { UseShiftAlarmsType } from './useShiftAlarms';

const SHIFT_ALARM_PICKER_WIDTH = 102;

const PickerContainer = styled(View)`
  min-width: ${SHIFT_ALARM_PICKER_WIDTH}px;
`;

const StyledTrashIcon = styled(TrashIcon)`
  width: 28px;
`;

const StyledRow = styled(Row)`
  justify-content: flex-end;
`;

const StyledEditablePicker = styled(EditablePicker)`
  max-width: ${SHIFT_ALARM_PICKER_WIDTH}px;
`;

type Props = {
  alarmModel: ShiftAlarmModelType;
  validateAlarm: (value: number) => boolean;
  alarmsModelCount: number;
} & Partial<UseShiftAlarmsType>;

const ShiftAlarmRow: React.FC<React.PropsWithChildren<Props>> = ({
  alarmModel,
  validateAlarm,
  removeAlarm,
  onChangeAlarmValue,
  onChangeAlarmUnit,
  alarmsModelCount,
}) => {
  const { toggleError, isFieldValid } = useSettingsErrorsContext();
  const ref = useRef(null);

  useEffect(() => {
    ref.current?.focus();
  }, []);

  return (
    <>
      <StyledRow>
        <PickerContainer>
          <StyledEditablePicker
            ref={ref}
            usePickerValueWhenDisplay
            options={
              alarmModel.unit.value === AlarmUnits.minutes
                ? SHIFT_ALARM_OPTIONS_IN_MINUTES
                : SHIFT_ALARM_OPTIONS_IN_HOURS
            }
            value={
              alarmModel.unit.value === AlarmUnits.hours
                ? Math.ceil(alarmModel.value / 60)
                : alarmModel.value
            }
            onChange={(value): void => {
              onChangeAlarmValue(value, alarmModel.id);
              toggleError(TabType.notifications, alarmModel.id, !validateAlarm(value));
            }}
            onBlur={(): void =>
              toggleError(TabType.notifications, alarmModel.id, !validateAlarm(alarmModel.value))
            }
            invalid={!isFieldValid(alarmModel.id)}
          />
        </PickerContainer>

        <PickerContainer>
          <Padding left={10}>
            <StyledEditablePicker
              options={SHIFT_ALARMS_UNIT_OPTIONS}
              value={alarmModel.unit.value}
              onChange={(value: AlarmUnits): void => onChangeAlarmUnit(value, alarmModel.id)}
            />
          </Padding>
        </PickerContainer>
        {alarmsModelCount > 1 && (
          <Clickable onClick={(): void => removeAlarm(alarmModel.id)}>
            <StyledTrashIcon />
          </Clickable>
        )}
      </StyledRow>
    </>
  );
};

export default ShiftAlarmRow;
